import "rc-dialog/assets/index.css";

import classnames from "classnames";
import Dialog from "rc-dialog";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";

import InformationButton from "../../components/InformationButton/InformationButton";
import LanguageButton, {
    Country
} from "../../components/LanguageButton/LanguageButton";
import MarqueeLabel from "../../components/MarqueeLabel/MarqueeLabel";
import RouteOrAction from "../../components/RouteOrAction/RouteOrAction";
import TitleButton from "../../components/TitleButton/TitleButton";
import {
    EventAction,
    EventCategory,
    EventLabel,
    GoogleTackingContext,
    TrackEvent
} from "../../services/googleTagManagerTracker";
import screenToRoute from "../../services/screenToRoute";

// tslint:disable-next-line:no-var-requires
const style = require("./StartScreen.scss");

export type Screen =
    | "START"
    | "ENTER_PIN"
    | "BETS"
    | "RESULTS"
    | "LIVE"
    | "MANUAL_PAYOUT"
    | "INFO"
    | "ACCOUNT"
    | "ACCOUNT_OVERVIEW"
    | "ENTER_PIN"
    | "CHANGE_PIN"
    | "CASHOUT"
    | "BACK"
    | "LIMITS_AND_CLOSURE"
    | "RETHINK_IDLE"
    | "RECOMMENDATIONS"
    | "TOP_COMBIS";

export interface StartScreenProps extends RouteComponentProps {
    readonly showForeignShopInfo: boolean;
    readonly bettingAllowed: boolean;
    readonly buttonsBlockedForDispatchTerminal?: boolean;
    readonly lwkButtonEnabled: boolean;
    readonly customerCardEnabled: boolean;
    readonly manualTicketCheckAllowed: boolean;
    readonly languages: Country[];
    readonly semiSelfService: boolean;
    readonly accountRoute?: Screen;
    readonly changeLanguage: (lang: Country) => void;
    readonly recommendationsEnabled: boolean;
    readonly resultsEnabled: boolean;
    readonly topCombisEnabled: boolean;
}

export interface StartScreenState {
    readonly waitingForLogin: boolean;
}

export class StartScreen extends React.Component<
    StartScreenProps & WithTranslation & { readonly trackEvent: TrackEvent },
    StartScreenState
> {
    private mousePosition: { x: number; y: number } | undefined;

    constructor(
        props: StartScreenProps &
            WithTranslation & { readonly trackEvent: TrackEvent }
    ) {
        super(props);

        this.state = {
            waitingForLogin: false
        };
    }

    public componentDidMount() {
        if (document) {
            document.addEventListener("click", this.mouseHandler, true);
        }
    }

    public componentWillUnmount() {
        if (document) {
            document.removeEventListener("click", this.mouseHandler);
        }
    }

    public componentDidUpdate(prevProps: StartScreenProps & WithTranslation) {
        if (
            this.state.waitingForLogin &&
            this.props.accountRoute &&
            !prevProps.accountRoute
        ) {
            this.setState({ waitingForLogin: false });
            this.props.history.push(screenToRoute(this.props.accountRoute));
        }
    }

    public render() {
        const { t } = this.props;
        return (
            <div className={classnames(style.startScreen)}>
                <div className={classnames(style.marquee)}>
                    {this.renderMarqueeLabel()}
                </div>
                <div className={classnames(style.menu)}>
                    {this.renderEvents()}
                    {this.renderNavigateToResults()}
                    {this.renderLiveEvents()}
                    {this.renderNavigateToAccount()}
                    {this.renderNavigateToCheckTicket()}
                    {this.renderNavigateToRecommendations()}
                    {this.renderTopCombis()}
                </div>
                <div className={classnames(style.languages)}>
                    {this.renderLanguageBar()}
                </div>
                <div className={classnames(style.info)}>
                    <Link to="/info">
                        <InformationButton />
                    </Link>
                </div>
                <Dialog
                    visible={this.state.waitingForLogin}
                    onClose={this.cancelLoginWaiting}
                    mousePosition={this.mousePosition}
                    animation="zoom"
                    maskAnimation="fade"
                    wrapClassName={classnames(style.darkDialog)}
                    title={t("please-login")}
                >
                    <p>{t("please-login-description")}</p>
                    <footer>
                        <button
                            className="cancel-waiting-for-login-button"
                            onClick={this.cancelLoginWaiting}
                        >
                            {t("cancel")}
                        </button>
                    </footer>
                </Dialog>
            </div>
        );
    }

    private trackBetsButtonClickEvent = () => {
        this.props.trackEvent(
            EventAction.ButtonClick,
            EventCategory.MainButton,
            EventLabel.Bets
        );
    };

    private trackResultsButtonClick = () => {
        this.props.trackEvent(
            EventAction.ButtonClick,
            EventCategory.MainButton,
            EventLabel.Results
        );
    };

    private trackLiveBetsButtonClick = () => {
        this.props.trackEvent(
            EventAction.ButtonClick,
            EventCategory.MainButton,
            EventLabel.LiveBets
        );
    };

    private waitingForLogin = () => {
        this.setState({ waitingForLogin: true });
    };

    private cancelLoginWaiting = () =>
        this.setState({
            waitingForLogin: false
        });

    private changeLanguage = (lang: Country) => {
        this.props.i18n.changeLanguage(lang.toLowerCase());
        this.props.changeLanguage(lang);
    };

    private renderNavigateToAccount() {
        return (
            !this.props.semiSelfService &&
            this.props.customerCardEnabled && (
                <RouteOrAction
                    isRoute={!!this.props.accountRoute}
                    route={screenToRoute(this.props.accountRoute)}
                    action={this.waitingForLogin}
                >
                    <TitleButton className="account">
                        {this.props.t("account")}
                    </TitleButton>
                </RouteOrAction>
            )
        );
    }

    private renderEvents() {
        return (
            this.props.buttonsBlockedForDispatchTerminal !== true && (
                <Link to="/bets">
                    <TitleButton
                        className="bets"
                        enabled={this.props.bettingAllowed}
                        onClick={this.trackBetsButtonClickEvent}
                    >
                        {this.props.t("bets")}
                    </TitleButton>
                </Link>
            )
        );
    }

    private renderLiveEvents() {
        return (
            this.props.buttonsBlockedForDispatchTerminal !== true &&
            this.props.lwkButtonEnabled && (
                <Link to="/live">
                    <TitleButton
                        className="live-bets"
                        enabled={this.props.bettingAllowed}
                        onClick={this.trackLiveBetsButtonClick}
                    >
                        {this.props.t("livebets")}
                    </TitleButton>
                </Link>
            )
        );
    }

    private renderNavigateToCheckTicket() {
        return (
            this.props.manualTicketCheckAllowed && (
                <Link to="/manual-payout">
                    <TitleButton className="check-ticket">
                        {this.props.t("checkTicket")}
                    </TitleButton>
                </Link>
            )
        );
    }

    private renderNavigateToRecommendations() {
        return (
            this.props.recommendationsEnabled && (
                <Link to="/recommendations">
                    <TitleButton className="bets-for-you">
                        {this.props.t("betsForYou")}
                    </TitleButton>
                </Link>
            )
        );
    }

    private renderTopCombis() {
        return (
            this.props.topCombisEnabled && (
                <Link to="/top_combis">
                    <TitleButton
                        className="top-combis"
                        btnLabelText={this.props.t("new")}
                    >
                        {this.props.t("top-combis")}
                    </TitleButton>
                </Link>
            )
        );
    }

    private renderNavigateToResults() {
        return (
            this.props.resultsEnabled && (
                <Link to="/results">
                    <TitleButton
                        className="results"
                        onClick={this.trackResultsButtonClick}
                    >
                        {this.props.t("results")}
                    </TitleButton>
                </Link>
            )
        );
    }

    private renderLanguageBar() {
        return this.props.languages.map(country => (
            <LanguageButton
                {...{
                    country,
                    key: country,
                    onClick: () => this.changeLanguage(country)
                }}
            />
        ));
    }

    private renderMarqueeLabel() {
        return (
            this.props.showForeignShopInfo && (
                <MarqueeLabel>{this.props.t("foreignShopInfo")}</MarqueeLabel>
            )
        );
    }
    private mouseHandler = (e: MouseEvent) =>
        (this.mousePosition = { x: e.pageX, y: e.pageY });
}

export default withRouter(
    withTranslation(
        "startScreen"
    )((props: StartScreenProps & WithTranslation) => (
        <GoogleTackingContext.Consumer>
            {trackEvent => <StartScreen {...{ ...props, trackEvent }} />}
        </GoogleTackingContext.Consumer>
    ))
);
